import { useEffect, useState } from "react";
import { api, RESOURCE_TYPES } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search, X } from "lucide-react";

export default function FilterBar({ filters, setFilters }) {
  const [allKeywords, setAllKeywords] = useState([]);

  useEffect(() => {
    api.keywords().then(setAllKeywords).catch(() => setAllKeywords([]));
  }, []);

  const toggleKeyword = (kw) => {
    setFilters((f) => {
      const has = f.keywords.includes(kw);
      return {
        ...f,
        keywords: has ? f.keywords.filter((k) => k !== kw) : [...f.keywords, kw],
      };
    });
  };

  return (
    <div className="flex flex-col gap-6" data-testid="library-filter-bar">
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            data-testid="library-search-input"
            value={filters.search}
            onChange={(e) =>
              setFilters((f) => ({ ...f, search: e.target.value }))
            }
            placeholder="Cerca per titolo, contenuto, keyword…"
            className="pl-9 h-11 bg-white"
          />
        </div>

        <Select
          value={filters.type || "all"}
          onValueChange={(v) =>
            setFilters((f) => ({ ...f, type: v === "all" ? "" : v }))
          }
        >
          <SelectTrigger
            data-testid="library-type-filter"
            className="h-11 w-full md:w-[180px] bg-white"
          >
            <SelectValue placeholder="Tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {RESOURCE_TYPES.map((t) => (
              <SelectItem key={t.value} value={t.value}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.sort}
          onValueChange={(v) => setFilters((f) => ({ ...f, sort: v }))}
        >
          <SelectTrigger
            data-testid="library-sort-filter"
            className="h-11 w-full md:w-[180px] bg-white"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Più recenti</SelectItem>
            <SelectItem value="date_asc">Più vecchi</SelectItem>
            <SelectItem value="title_asc">Titolo A-Z</SelectItem>
            <SelectItem value="type_asc">Per tipo</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {allKeywords.length > 0 && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500">
              Filtra per keyword
            </span>
            {filters.keywords.length > 0 && (
              <button
                data-testid="clear-keywords"
                onClick={() => setFilters((f) => ({ ...f, keywords: [] }))}
                className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1"
              >
                <X size={12} /> Pulisci ({filters.keywords.length})
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {allKeywords.slice(0, 40).map((k) => {
              const active = filters.keywords.includes(k.keyword);
              return (
                <button
                  key={k.keyword}
                  data-testid={`keyword-chip-${k.keyword}`}
                  onClick={() => toggleKeyword(k.keyword)}
                  className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                    active
                      ? "bg-[#002FA7] text-white border-[#002FA7]"
                      : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50 hover:border-zinc-300"
                  }`}
                >
                  {k.keyword}
                  <span
                    className={`ml-1.5 ${active ? "text-white/70" : "text-zinc-400"}`}
                  >
                    {k.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
